import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useParams, Link } from "react-router-dom"
import { Document } from "react-pdf"
import { PDF_OPTIONS } from "../lib/pdfWorker"
import "react-pdf/dist/Page/AnnotationLayer.css"
import "react-pdf/dist/Page/TextLayer.css"

import { AppShell } from "../components/AppShell"
import { BookPageOverlay, type CellGeom } from "../components/BookPageOverlay"
import { JsonCodeEditor } from "../components/JsonCodeEditor"
import { useApi } from "../api/hooks"
import { openForm, saveTemplateCells, type FormInstance } from "../api/templates"
import type { CellMeta, CellType } from "../api/book"
import { detectFields } from "../lib/detectFields"

const btn: React.CSSProperties = {
  height: 30, padding: "0 10px", display: "inline-grid", placeItems: "center",
  borderRadius: 6, border: "1px solid var(--border)", background: "var(--surface)",
  color: "var(--text)", cursor: "pointer", fontSize: 13,
}
const inp: React.CSSProperties = {
  height: 28, padding: "0 6px", border: "1px solid var(--border)", borderRadius: 6,
  background: "var(--bg)", color: "var(--text)",
}

type Meta = { type?: CellType; decimals?: number; min?: number; max?: number; label?: string; section?: string; ecoicop?: string[] }

export function CellEditorPage() {
  const { templateId = "istat-1559-2024", instanceKey = "main" } = useParams<{ templateId: string; instanceKey: string }>()
  const api = useApi()
  const [form, setForm] = useState<FormInstance | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [pdfData, setPdfData] = useState<{ data: Uint8Array } | null>(null)
  const [pageNum, setPageNum] = useState(1)
  const [scale, setScale] = useState(1.4)
  const [baseWidth, setBaseWidth] = useState(700)
  const centerRef = useRef<HTMLDivElement | null>(null)

  const [edits, setEdits] = useState<Record<string, CellGeom>>({})
  const [renames, setRenames] = useState<Record<string, string>>({})
  const [deleted, setDeleted] = useState<Record<string, true>>({})
  const [meta, setMeta] = useState<Record<string, Meta>>({})
  const [added, setAdded] = useState<Record<string, CellMeta[]>>({})
  const [selected, setSelected] = useState<Set<string>>(() => new Set())
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle")
  const [tab, setTab] = useState<"graphic" | "json">("graphic")
  const [jsonText, setJsonText] = useState<string>("")
  const [jsonErr, setJsonErr] = useState<string | null>(null)
  const [guides, setGuides] = useState<{ v: number[]; h: number[] }>({ v: [], h: [] })
  const [pdfDoc, setPdfDoc] = useState<unknown>(null)
  const [recognizing, setRecognizing] = useState(false)
  const dragSnap = useRef<{ codes: string[]; geoms: Record<string, CellGeom>; primary: string } | null>(null)

  useEffect(() => {
    openForm(api, templateId, instanceKey).then(setForm)
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)))
  }, [api, templateId, instanceKey])

  // Download the PDF ONCE per template and feed it to react-pdf as a Uint8Array.
  // A blob: URL would violate the prod connect-src CSP (which has no blob:), so
  // pdf.js couldn't fetch it → "PDF non disponibile". Bytes sidestep that and also
  // avoid re-fetching when the presigned template_url changes on token refresh.
  // The fake main-thread worker never detaches the buffer, so this is safe.
  const pdfLoadedFor = useRef<string | null>(null)
  useEffect(() => {
    const tid = form?.template?.template_id
    const url = form?.template_url
    if (!tid || !url || pdfLoadedFor.current === tid) return
    pdfLoadedFor.current = tid
    fetch(url).then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.arrayBuffer() })
      .then((buffer) => setPdfData({ data: new Uint8Array(buffer) }))
      .catch((e) => { pdfLoadedFor.current = null; setErr(e instanceof Error ? e.message : String(e)) })
  }, [form?.template?.template_id, form?.template_url])

  // Re-measure on tab change too: the graphic container is unmounted while the
  // JSON tab is active, so the observer must re-attach to the new element when
  // returning to Grafico (otherwise baseWidth stays stale → page renders tiny).
  useEffect(() => {
    if (tab !== "graphic") return
    const el = centerRef.current
    if (!el) return
    const update = () => setBaseWidth(Math.max(360, el.clientWidth - 32))
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [form, tab])

  const totalPages = form?.template.total_pages ?? 1
  const renderedWidth = Math.round(baseWidth * scale)

  // Effective cells for the current page = originals + added, minus deleted.
  const pageCells: CellMeta[] = useMemo(() => {
    const base = form?.cells.pages[String(pageNum)] ?? []
    const extra = added[String(pageNum)] ?? []
    return [...base, ...extra]
  }, [form, pageNum, added])
  const cells = pageCells.filter((c) => !deleted[c.code])

  const geomOf = useCallback((code: string): CellGeom | null => {
    if (edits[code]) return edits[code]
    const c = pageCells.find((x) => x.code === code)
    return c ? { x: c.x, y: c.y, w: c.w, h: c.h } : null
  }, [edits, pageCells])

  // ----- selection -----
  const onSelect = useCallback((code: string, additive: boolean) => {
    setSelected((prev) => {
      if (!additive) return new Set([code])
      const n = new Set(prev)
      n.has(code) ? n.delete(code) : n.add(code)
      return n
    })
  }, [])
  const onMarquee = useCallback((codes: string[], additive: boolean) => {
    setSelected((prev) => {
      if (!additive) return new Set(codes)
      const n = new Set(prev)
      codes.forEach((c) => n.add(c))
      return n
    })
  }, [])

  // ----- drag/move (group-aware) -----
  const onDragStart = useCallback((code: string) => {
    setSelected((prev) => {
      const inSel = prev.has(code)
      const group = inSel && prev.size > 1 ? [...prev] : [code]
      const geoms: Record<string, CellGeom> = {}
      for (const c of group) {
        const g = geomOf(c)
        if (g) geoms[c] = g
      }
      dragSnap.current = { codes: Object.keys(geoms), geoms, primary: code }
      return inSel ? prev : new Set([code])
    })
  }, [geomOf])

  const onMove = useCallback((dx: number, dy: number) => {
    const snap = dragSnap.current
    if (!snap) return
    const SNAP = 0.004 // ~0.4% of the page
    const p = snap.geoms[snap.primary]
    // Edges of every cell NOT being dragged, as snap targets.
    const dragged = new Set(snap.codes)
    const vt: number[] = [], ht: number[] = []
    for (const c of pageCells) {
      if (dragged.has(c.code)) continue
      const g = geomOf(c.code)
      if (!g) continue
      vt.push(g.x, g.x + g.w / 2, g.x + g.w)
      ht.push(g.y, g.y + g.h / 2, g.y + g.h)
    }
    // Snap the primary cell's left/center/right (and top/middle/bottom).
    const snap1 = (base: number, w: number, targets: number[]) => {
      const edges = [base, base + w / 2, base + w]
      let best: { adj: number; line: number } | null = null
      for (const e of edges) for (const t of targets) {
        const d = t - e
        if (Math.abs(d) <= SNAP && (!best || Math.abs(d) < Math.abs(best.adj))) best = { adj: d, line: t }
      }
      return best
    }
    let ndx = dx, ndy = dy
    const gv: number[] = [], gh: number[] = []
    const sx = snap1(p.x + dx, p.w, vt)
    if (sx) { ndx = dx + sx.adj; gv.push(sx.line) }
    const sy = snap1(p.y + dy, p.h, ht)
    if (sy) { ndy = dy + sy.adj; gh.push(sy.line) }
    setGuides({ v: gv, h: gh })
    setEdits((prev) => {
      const n = { ...prev }
      for (const c of snap.codes) {
        const s = snap.geoms[c]
        n[c] = { ...s, x: s.x + ndx, y: s.y + ndy }
      }
      return n
    })
  }, [geomOf, pageCells])

  const onDragEnd = useCallback(() => {
    dragSnap.current = null
    setGuides({ v: [], h: [] })
  }, [])
  const onResize = useCallback((code: string, g: CellGeom) => {
    setEdits((prev) => ({ ...prev, [code]: g }))
  }, [])

  // ----- delete / add -----
  const deleteSelected = useCallback(() => {
    setSelected((sel) => {
      if (sel.size) setDeleted((prev) => { const n = { ...prev }; sel.forEach((c) => { n[c] = true }); return n })
      return new Set()
    })
  }, [])

  const addCell = useCallback(() => {
    const existing = new Set<string>([
      ...Object.values(form?.cells.pages ?? {}).flat().map((c) => c.code),
      ...Object.values(added).flat().map((c) => c.code),
    ])
    let i = 1
    let code = `new${i}`
    while (existing.has(code)) { i += 1; code = `new${i}` }
    const cell: CellMeta = { code, day: 0, item: 0, x: 0.4, y: 0.4, w: 0.08, h: 0.027, type: "decimal", decimals: 2 }
    setAdded((prev) => ({ ...prev, [String(pageNum)]: [...(prev[String(pageNum)] ?? []), cell] }))
    setSelected(new Set([code]))
  }, [form, added, pageNum])

  // Auto-detect filled boxes on the current page (pdf.js operator list).
  const recognize = useCallback(async () => {
    if (!pdfDoc) return
    setRecognizing(true)
    try {
      const rects = await detectFields(pdfDoc, pageNum)
      const used = new Set<string>([
        ...Object.values(form?.cells.pages ?? {}).flat().map((c) => c.code),
        ...Object.values(added).flat().map((c) => c.code),
      ])
      let n = 0
      const nextCode = () => { let code: string; do { n += 1; code = `p${pageNum}f${n}` } while (used.has(code)); used.add(code); return code }
      const newCells: CellMeta[] = rects.map((r) => ({ code: nextCode(), day: 0, item: 0, x: r.x, y: r.y, w: r.w, h: r.h, type: "decimal", decimals: 2, ...(r.label ? { label: r.label } : {}), ...(r.section ? { section: r.section } : {}) }))
      setAdded((prev) => ({ ...prev, [String(pageNum)]: [...(prev[String(pageNum)] ?? []), ...newCells] }))
      setSelected(new Set(newCells.map((c) => c.code)))
    } finally { setRecognizing(false) }
  }, [pdfDoc, pageNum, form, added])

  // Apply a width/height to all selected cells (absolute % if `value`, else nudge by `delta` %).
  const sizeSelected = useCallback((k: "w" | "h", value: number | null, delta = 0) => {
    setSelected((sel) => {
      if (sel.size) {
        setEdits((prev) => {
          const n = { ...prev }
          for (const c of sel) {
            const g = n[c] ?? geomOf(c)
            if (!g) continue
            const nv = value != null ? value / 100 : g[k] + delta / 100
            n[c] = { ...g, [k]: Math.max(0.005, nv) }
          }
          return n
        })
      }
      return sel
    })
  }, [geomOf])

  const moveSelected = useCallback((dx: number, dy: number) => {
    setSelected((sel) => {
      if (sel.size) {
        setEdits((prev) => {
          const n = { ...prev }
          for (const c of sel) {
            const g = n[c] ?? geomOf(c)
            if (g) n[c] = { ...g, x: g.x + dx, y: g.y + dy }
          }
          return n
        })
      }
      return sel
    })
  }, [geomOf])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = document.activeElement
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT")) return
      if (!selected.size) return
      if (e.key === "Delete" || e.key === "Backspace") { e.preventDefault(); deleteSelected(); return }
      const step = e.shiftKey ? 0.01 : 0.002
      const moves: Record<string, [number, number]> = {
        ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step],
      }
      const d = moves[e.key]
      if (d) { e.preventDefault(); moveSelected(d[0], d[1]) }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [selected, deleteSelected, moveSelected])

  // ----- single-selection panel data -----
  const single = selected.size === 1 ? [...selected][0] : null
  const singleCell = single ? pageCells.find((c) => c.code === single) ?? null : null
  const singleGeom = single ? geomOf(single) : null
  // Representative size for a multi-selection (first selected cell).
  const multiGeom = selected.size > 1 ? geomOf([...selected][0]) : null
  const singleMeta: Meta = useMemo(() => {
    if (!single || !singleCell) return {}
    const base: Meta = { type: singleCell.type, decimals: singleCell.decimals, min: singleCell.min, max: singleCell.max, label: singleCell.label, section: singleCell.section, ecoicop: singleCell.ecoicop }
    return { ...base, ...meta[single] }
  }, [single, singleCell, meta])

  const setField = (k: keyof CellGeom, vPct: number) => {
    if (!single || !singleGeom) return
    setEdits((prev) => ({ ...prev, [single]: { ...singleGeom, [k]: vPct / 100 } }))
  }
  const nudge = (k: keyof CellGeom, d: number) => {
    if (!single || !singleGeom) return
    setEdits((prev) => ({ ...prev, [single]: { ...singleGeom, [k]: Math.max(0, singleGeom[k] + d / 100) } }))
  }
  const setMetaField = (patch: Meta) => {
    if (!single) return
    setMeta((prev) => ({ ...prev, [single]: { ...singleMeta, ...patch } }))
  }

  // Representative type/domain for a multi-selection (first selected cell).
  const multiMeta: Meta = useMemo(() => {
    if (selected.size <= 1) return {}
    const code = [...selected][0]
    const cell = pageCells.find((c) => c.code === code)
    const base: Meta = cell ? { type: cell.type, decimals: cell.decimals, min: cell.min, max: cell.max } : {}
    return { ...base, ...meta[code] }
  }, [selected, pageCells, meta])

  const setMetaMany = (patch: Meta) => {
    setMeta((prev) => {
      const n = { ...prev }
      for (const code of selected) {
        const cell = pageCells.find((c) => c.code === code)
        const base: Meta = cell ? { type: cell.type, decimals: cell.decimals, min: cell.min, max: cell.max } : {}
        n[code] = { ...base, ...(prev[code] ?? {}), ...patch }
      }
      return n
    })
  }

  // ----- build + persist -----
  const buildBundle = useCallback(() => {
    if (!form) return null
    const round = (n: number) => Math.round(n * 1e5) / 1e5
    const pages: Record<string, CellMeta[]> = {}
    const pageKeys = new Set([...Object.keys(form.cells.pages), ...Object.keys(added)])
    for (const pn of pageKeys) {
      const list = [...(form.cells.pages[pn] ?? []), ...(added[pn] ?? [])]
      pages[pn] = list.filter((c) => !deleted[c.code]).map((c) => {
        const e = edits[c.code]
        const newCode = renames[c.code] ?? c.code
        const dm = /^d(\d{2})(\d{3})$/.exec(newCode)
        const m: Meta = { type: c.type, decimals: c.decimals, min: c.min, max: c.max, label: c.label, section: c.section, ecoicop: c.ecoicop, ...meta[c.code] }
        const out: CellMeta = {
          ...c, code: newCode,
          day: dm ? Number(dm[1]) : c.day,
          item: dm ? Number(dm[2]) : c.item,
          x: round(e ? e.x : c.x), y: round(e ? e.y : c.y),
          w: round(e ? e.w : c.w), h: round(e ? e.h : c.h),
        }
        if (m.type) out.type = m.type; else delete out.type
        if (m.type === "decimal" && m.decimals != null) out.decimals = m.decimals; else delete out.decimals
        if (m.min != null) out.min = m.min; else delete out.min
        if (m.max != null) out.max = m.max; else delete out.max
        if (m.label) out.label = m.label; else delete out.label
        if (m.section) out.section = m.section; else delete out.section
        if (m.ecoicop && m.ecoicop.length) out.ecoicop = m.ecoicop; else delete out.ecoicop
        return out
      })
    }
    const total = Object.values(pages).reduce((n, l) => n + l.length, 0)
    return { ...form.cells, total_cells: total, pages }
  }, [form, edits, renames, deleted, meta, added])

  const exportCells = useCallback(() => {
    const bundle = buildBundle()
    if (!form || !bundle) return
    const a = document.createElement("a")
    a.href = URL.createObjectURL(new Blob([JSON.stringify(bundle)], { type: "application/json" }))
    a.download = `${form.template.template_id}-cells.json`
    a.click()
    URL.revokeObjectURL(a.href)
  }, [form, buildBundle])

  const openJson = () => { const b = buildBundle(); setJsonErr(null); setJsonText(b ? JSON.stringify(b, null, 2) : ""); setTab("json") }

  // Apply pasted/edited JSON as the new working cells (does not save to server).
  const applyJson = () => {
    try {
      const parsed = JSON.parse(jsonText)
      if (!parsed || typeof parsed !== "object" || !parsed.pages) throw new Error("manca 'pages'")
      setForm((f) => (f ? { ...f, cells: parsed } : f))
      setEdits({}); setRenames({}); setDeleted({}); setMeta({}); setAdded({}); setSelected(new Set())
      setJsonErr(null); setTab("graphic")
    } catch (e) {
      setJsonErr(e instanceof Error ? e.message : String(e))
    }
  }

  const saveCells = useCallback(async () => {
    const bundle = buildBundle()
    if (!bundle) return
    setSaveState("saving")
    try {
      await saveTemplateCells(api, templateId, bundle)
      // Saved state becomes the new baseline → clear all pending edits (count → 0).
      setForm((f) => (f ? { ...f, cells: bundle } : f))
      setEdits({}); setRenames({}); setDeleted({}); setMeta({}); setAdded({})
      setSaveState("saved"); window.setTimeout(() => setSaveState("idle"), 2000)
    } catch { setSaveState("error") }
  }, [api, templateId, buildBundle])

  const reset = () => { setEdits({}); setRenames({}); setDeleted({}); setMeta({}); setAdded({}); setSelected(new Set()) }
  const editCount = Object.keys(edits).length + Object.keys(renames).length + Object.keys(deleted).length
    + Object.keys(meta).length + Object.values(added).reduce((n, l) => n + l.length, 0)

  return (
    <AppShell fluid>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <h1 style={{ margin: 0, fontSize: 20 }}>Editor celle</h1>
        <span style={{ color: "var(--muted)", fontSize: 13 }}>{form?.template.name ?? templateId}</span>
        <Link to={`/forms/${templateId}/${instanceKey}`} style={{ ...btn, textDecoration: "none" }}>↩ Diario</Link>
        <div style={{ display: "inline-flex", border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden" }}>
          <button onClick={() => setTab("graphic")} style={{ ...btn, height: 30, border: 0, borderRadius: 0, background: tab === "graphic" ? "var(--accent)" : "var(--surface)", color: tab === "graphic" ? "var(--accent-fg)" : "var(--text)", fontWeight: 600 }}>Grafico</button>
          <button onClick={openJson} style={{ ...btn, height: 30, border: 0, borderRadius: 0, background: tab === "json" ? "var(--accent)" : "var(--surface)", color: tab === "json" ? "var(--accent-fg)" : "var(--text)", fontWeight: 600 }}>JSON</button>
        </div>

        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <button style={btn} onClick={addCell}>＋ Cella</button>
          <button style={btn} onClick={recognize} disabled={!pdfDoc || recognizing} title="Rileva automaticamente le caselle di questa pagina">
            {recognizing ? "Rilevo…" : "🔍 Riconosci"}
          </button>
          {selected.size > 0 && (
            <button style={{ ...btn, color: "#d12", borderColor: "#d12" }} onClick={deleteSelected}>🗑 Elimina ({selected.size})</button>
          )}
          <span style={{ width: 1, height: 20, background: "var(--border)" }} />
          <button style={btn} onClick={() => setPageNum((p) => Math.max(1, p - 1))} disabled={pageNum <= 1}>‹</button>
          <input type="number" min={1} max={totalPages} value={pageNum}
            onChange={(e) => setPageNum(Math.min(totalPages, Math.max(1, Number(e.target.value) || 1)))}
            style={{ ...inp, width: 54, textAlign: "center" }} />
          <span style={{ fontSize: 12, color: "var(--muted)" }}>/ {totalPages}</span>
          <button style={btn} onClick={() => setPageNum((p) => Math.min(totalPages, p + 1))} disabled={pageNum >= totalPages}>›</button>
          <span style={{ width: 1, height: 20, background: "var(--border)" }} />
          <button style={btn} onClick={() => setScale((s) => Math.max(0.1, Math.round((s - 0.1) * 10) / 10))}>−</button>
          <span style={{ fontSize: 12, minWidth: 40, textAlign: "center" }}>{Math.round(scale * 100)}%</span>
          <button style={btn} onClick={() => setScale((s) => Math.min(4, Math.round((s + 0.1) * 10) / 10))}>+</button>
          <span style={{ width: 1, height: 20, background: "var(--border)" }} />
          <span style={{ fontSize: 12, color: "var(--muted)" }}>{editCount} mod.</span>
          <button
            style={{ ...btn, fontWeight: 600, background: "var(--accent)", color: "var(--accent-fg)", borderColor: "transparent", opacity: saveState === "saving" || editCount === 0 ? 0.6 : 1 }}
            onClick={saveCells} disabled={saveState === "saving" || editCount === 0}>
            {saveState === "saving" ? "Salvo…" : saveState === "saved" ? "✓ Salvato" : saveState === "error" ? "✗ Errore" : "💾 Salva"}
          </button>
          <button style={btn} onClick={exportCells} title="Scarica JSON">⬇</button>
          <button style={btn} onClick={reset} title="Annulla tutte le modifiche">↺</button>
        </div>
      </div>

      {err && <p style={{ color: "#ff7a85" }}>{err}</p>}
      {!err && !form && <p style={{ color: "var(--muted)" }}>Carico…</p>}

      {form && tab === "json" && (
        <div style={{ display: "flex", flexDirection: "column", height: "calc(100dvh - 150px)" }}>
          <JsonCodeEditor value={jsonText} onChange={setJsonText} style={{ flex: 1 }} />
          {jsonErr && <p style={{ color: "#ff7a85", margin: "8px 0 0" }}>JSON non valido: {jsonErr}</p>}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 10 }}>
            <span style={{ marginRight: "auto", color: "var(--muted)", fontSize: 12, alignSelf: "center" }}>
              Modifica/incolla e premi <strong>Applica</strong> (carica nell'editor). Per salvare su server poi <strong>💾 Salva</strong>.
            </span>
            <button style={btn} onClick={() => navigator.clipboard?.writeText(jsonText)}>Copia</button>
            <button style={{ ...btn, background: "var(--accent)", color: "var(--accent-fg)", borderColor: "transparent", fontWeight: 600 }} onClick={applyJson}>Applica</button>
          </div>
        </div>
      )}

      {form && tab === "graphic" && (
        <div style={{ display: "flex", gap: 16, alignItems: "flex-start", height: "calc(100dvh - 150px)" }}>
          <div ref={centerRef} style={{ flex: 1, height: "100%", overflow: "auto", padding: 16, background: "var(--surface)", borderRadius: "var(--radius)", border: "1px solid var(--border)" }}>
            <div style={{ display: "flex", justifyContent: "safe center" }}>
              <Document file={pdfData} options={PDF_OPTIONS} onLoadSuccess={(pdf) => setPdfDoc(pdf)} onLoadError={(e) => console.error("[pdf] load error", e)} loading={<p style={{ color: "var(--muted)" }}>Carico il PDF…</p>} error={<p style={{ color: "#ff7a85" }}>PDF non disponibile</p>}>
                <BookPageOverlay
                  key={pageNum}
                  pageIndex={pageNum - 1}
                  pageNum={pageNum}
                  width={renderedWidth}
                  cells={cells}
                  values={{}}
                  onChange={() => {}}
                  editMode
                  edits={edits}
                  onCellGeom={onResize}
                  selected={selected}
                  onSelect={onSelect}
                  onMarquee={onMarquee}
                  onDragStart={onDragStart}
                  onMove={onMove}
                  onDragEnd={onDragEnd}
                  guides={guides}
                  labels={renames}
                />
              </Document>
            </div>
          </div>

          <aside style={{ width: 280, flexShrink: 0, fontSize: 13 }}>
            <div style={{ border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: 12, background: "var(--surface)" }}>
              {selected.size > 1 ? (
                <>
                  <div style={{ marginBottom: 8 }}><strong>{selected.size}</strong> celle selezionate</div>
                  {multiGeom && (["w", "h"] as const).map((k) => (
                    <div key={k} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                      <label style={{ width: 14, textTransform: "uppercase", color: "var(--muted)" }}>{k}</label>
                      <button style={{ ...btn, height: 26, padding: "0 7px" }} onClick={() => sizeSelected(k, null, -0.1)}>−</button>
                      <input type="number" step={0.1} value={Math.round(multiGeom[k] * 1e5) / 1e3}
                        onChange={(e) => sizeSelected(k, Number(e.target.value))}
                        style={{ ...inp, width: 78, textAlign: "right" }} />
                      <span style={{ color: "var(--muted)" }}>%</span>
                      <button style={{ ...btn, height: 26, padding: "0 7px" }} onClick={() => sizeSelected(k, null, 0.1)}>+</button>
                    </div>
                  ))}
                  <hr style={{ border: 0, borderTop: "1px solid var(--border)", margin: "10px 0" }} />
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                    <label style={{ width: 56, color: "var(--muted)" }}>Tipo</label>
                    <select value={multiMeta.type ?? "decimal"} onChange={(e) => setMetaMany({ type: e.target.value as CellType })} style={{ ...inp, flex: 1 }}>
                      <option value="string">Stringa</option>
                      <option value="int">Intero</option>
                      <option value="decimal">Decimale</option>
                    </select>
                  </div>
                  {multiMeta.type === "decimal" && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                      <label style={{ width: 56, color: "var(--muted)" }}>Decimali</label>
                      <input type="number" min={0} max={6} value={multiMeta.decimals ?? 2}
                        onChange={(e) => setMetaMany({ decimals: Math.max(0, Number(e.target.value) || 0) })} style={{ ...inp, width: 78 }} />
                    </div>
                  )}
                  {multiMeta.type !== "string" && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                      <label style={{ width: 56, color: "var(--muted)" }}>Dominio</label>
                      <input type="number" placeholder="min" value={multiMeta.min ?? ""}
                        onChange={(e) => setMetaMany({ min: e.target.value === "" ? undefined : Number(e.target.value) })} style={{ ...inp, width: 70 }} />
                      <span style={{ color: "var(--muted)" }}>…</span>
                      <input type="number" placeholder="max" value={multiMeta.max ?? ""}
                        onChange={(e) => setMetaMany({ max: e.target.value === "" ? undefined : Number(e.target.value) })} style={{ ...inp, width: 70 }} />
                    </div>
                  )}
                  <p style={{ color: "var(--muted)", fontSize: 12, margin: "8px 0 0" }}>
                    W/H, tipo e dominio si applicano a tutte le selezionate. Trascina o usa le frecce per spostarle insieme.
                  </p>
                  <button style={{ ...btn, width: "100%", marginTop: 8, color: "#d12", borderColor: "#d12", fontWeight: 600 }} onClick={deleteSelected}>🗑 Elimina {selected.size}</button>
                </>
              ) : !single || !singleGeom ? (
                <p style={{ color: "var(--muted)", margin: 0 }}>Clicca una casella (o trascina un rettangolo per selezionarne più). <strong>＋ Cella</strong> per aggiungerne una.</p>
              ) : (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                    <label style={{ color: "var(--muted)" }}>Codice</label>
                    <input type="text" value={renames[single] ?? single}
                      onChange={(e) => { const v = e.target.value.trim(); setRenames((p) => { const n = { ...p }; if (v && v !== single) n[single] = v; else delete n[single]; return n }) }}
                      style={{ ...inp, flex: 1, fontFamily: "ui-monospace, monospace" }} />
                  </div>

                  {(["x", "y", "w", "h"] as const).map((k) => (
                    <div key={k} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                      <label style={{ width: 14, textTransform: "uppercase", color: "var(--muted)" }}>{k}</label>
                      <button style={{ ...btn, height: 26, padding: "0 7px" }} onClick={() => nudge(k, -0.1)}>−</button>
                      <input type="number" step={0.1} value={Math.round(singleGeom[k] * 1e5) / 1e3}
                        onChange={(e) => setField(k, Number(e.target.value))}
                        style={{ ...inp, width: 78, textAlign: "right" }} />
                      <span style={{ color: "var(--muted)" }}>%</span>
                      <button style={{ ...btn, height: 26, padding: "0 7px" }} onClick={() => nudge(k, 0.1)}>+</button>
                    </div>
                  ))}

                  <hr style={{ border: 0, borderTop: "1px solid var(--border)", margin: "10px 0" }} />

                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                    <label style={{ width: 56, color: "var(--muted)" }}>Tipo</label>
                    <select value={singleMeta.type ?? "decimal"} onChange={(e) => setMetaField({ type: e.target.value as CellType })} style={{ ...inp, flex: 1 }}>
                      <option value="string">Stringa</option>
                      <option value="int">Intero</option>
                      <option value="decimal">Decimale</option>
                    </select>
                  </div>
                  {singleMeta.type === "decimal" && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                      <label style={{ width: 56, color: "var(--muted)" }}>Decimali</label>
                      <input type="number" min={0} max={6} value={singleMeta.decimals ?? 2}
                        onChange={(e) => setMetaField({ decimals: Math.max(0, Number(e.target.value) || 0) })} style={{ ...inp, width: 78 }} />
                    </div>
                  )}
                  {singleMeta.type !== "string" && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                      <label style={{ width: 56, color: "var(--muted)" }}>Dominio</label>
                      <input type="number" placeholder="min" value={singleMeta.min ?? ""}
                        onChange={(e) => setMetaField({ min: e.target.value === "" ? undefined : Number(e.target.value) })} style={{ ...inp, width: 70 }} />
                      <span style={{ color: "var(--muted)" }}>…</span>
                      <input type="number" placeholder="max" value={singleMeta.max ?? ""}
                        onChange={(e) => setMetaField({ max: e.target.value === "" ? undefined : Number(e.target.value) })} style={{ ...inp, width: 70 }} />
                    </div>
                  )}

                  <hr style={{ border: 0, borderTop: "1px solid var(--border)", margin: "10px 0" }} />
                  <div style={{ marginBottom: 6 }}>
                    <label style={{ display: "block", color: "var(--muted)", marginBottom: 2 }}>Etichetta (significato)</label>
                    <input type="text" value={singleMeta.label ?? ""} placeholder="es. Piatti pronti a base di pesce"
                      onChange={(e) => setMetaField({ label: e.target.value || undefined })} style={{ ...inp, width: "100%" }} />
                  </div>
                  <div style={{ marginBottom: 6 }}>
                    <label style={{ display: "block", color: "var(--muted)", marginBottom: 2 }}>ECOICOP (override, separati da virgola)</label>
                    <input type="text" value={(singleMeta.ecoicop ?? []).join(", ")} placeholder="es. 01.1.3, 01.1.4"
                      onChange={(e) => setMetaField({ ecoicop: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} style={{ ...inp, width: "100%", fontFamily: "ui-monospace, monospace" }} />
                  </div>
                  <button style={{ ...btn, width: "100%", marginTop: 8, color: "#d12", borderColor: "#d12", fontWeight: 600 }} onClick={deleteSelected}>🗑 Elimina cella</button>
                </>
              )}
            </div>
            <p style={{ color: "var(--muted)", fontSize: 12, marginTop: 10 }}>
              Trascina = sposta (anche in gruppo) · <strong>frecce</strong> = sposta selezione (Shift = passo grande) · angolo = ridimensiona · rettangolo su area vuota = seleziona più celle · <strong>Shift+clic</strong> = aggiungi · <strong>Canc</strong> = elimina. Premi <strong>💾 Salva</strong> al termine.
            </p>
          </aside>
        </div>
      )}
    </AppShell>
  )
}
